/*************************************************************************
* Copyright (c) 2025 Omnineura AI Technologies Pvt Limited.
* All rights reserved.
* This code is licensed under the 'Omnineura AI License' found in the LICENSE file.
* Unauthorized copying, sharing, or use of this code is prohibited.
* The intellectual and technical concepts are proprietary to Omnineura AI Technologies Pvt Limited.
* Author: surya@Omnineura.ai
* Date: 11-09-2025
*************************************************************************/

import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

// Create axios instance with credentials
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // ✅ ensures cookies always sent
});

// ----------------- RAG Config -----------------
export const getRagconfigApi = async () => {
  const res = await axiosInstance.get("/config/rag-config");
  return res.data;
};

export const saveRagconfigApi = async (ragConfig) => {
  const res = await axiosInstance.post(
    "/config/rag-config",
    { rag_config: ragConfig },
    { headers: { "Content-Type": "application/json" } }
  );
  return res.data;
};

// ----------------- Documents -----------------
export const uploadDocumentsApi = async (files, onUploadProgress) => {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));

  const res = await axiosInstance.post(
    "/documents/upload-multipart",
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onUploadProgress(percentCompleted);
        }
      }
    }
  );
  return res.data;
};

export const checkUploadStatusApi = async (taskId) => {
  const res = await axiosInstance.get(`/tasks/${taskId}/status`);
  return res.data;
};

export const getDocumentApi = async () => {
  //MADE documents to documents/list
  const res = await axiosInstance.get("/documents/list");
  return res.data;
};

export const deleteDocumentApi = async (documentId) => {
  const res = await axiosInstance.delete(`/documents/${documentId}`);
  return res.data;
};

export const getDocumentContentApi = async (uuid) => {
  const res = await axiosInstance.post(
    "/documents/get_content",
    { uuid },
    { headers: { "Content-Type": "application/json" } }
  );
  return res.data;
};

// ----------------- Knowledge Base -----------------
export const createKnowledgeBaseApi = async (payload) => {

  const res = await axiosInstance.post(
    "/collections/create",
    payload,
    { headers: { "Content-Type": "application/json" } }
  );
  return res.data;
};

export const getAllKnowledgeBaseApi = async () => {
  const res = await axiosInstance.get("/collections/list");
  return res.data;
};


export const getActiveKnowledgeBaseApi = async () => {
  const res = await axiosInstance.get("/collections/active");
  return res.data;
}


export const activateKnowledgeBaseApi = async (body) => {
  const res = await axiosInstance.post(
    "/collections/activate",
    body,
    { headers: { "Content-Type": "application/json" } }
  );
  return res.data;
};

export const deleteKnowledgeBaseApi = async (collection_name) => {
  const res = await axiosInstance.delete(
    `/collections/${collection_name}`,
    { headers: { "Content-Type": "application/json" } }
  );
  return res.data;
}

// ----------------- Chat -----------------
export const createNewChatSessionApi = async (payload) => {
  const res = await axiosInstance.post(
    "/chat/sessions",
    payload,
    { headers: { "Content-Type": "application/json" } }
  );
  return res.data;
};

export const getAllChatSessionsApi = async () => {
  const res = await axiosInstance.get("/chat/sessions");
  return res.data;
};

export const deleteChatSessionApi = async (sessionId) => {
  const res = await axiosInstance.delete(`/chat/sessions/${sessionId}`);
  return res.data;
};

export const getChatSessionMessagesApi = async (sessionId) => {
  const res = await axiosInstance.get(`/chat/sessions/${sessionId}/messages`);
  return res.data;
};

export const sendChatMessageApi = async (payload) => {
  const res = await axiosInstance.post(
    "/chat/message",
    payload,
    { headers: { "Content-Type": "application/json" } }
  );
  return res.data;
};

export const streamChatMessageApi = async (payload, handlers = {}) => {
  const { onMeta, onChunk, onDone, onError } = handlers;
  const response = await fetch(`${API_BASE_URL}/chat/message/stream`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok || !response.body) {
    const errText = await response.text();
    throw new Error(errText || `Stream request failed with status ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop() || "";

    for (const event of events) {
      const line = event
        .split("\n")
        .find((entry) => entry.startsWith("data:"));

      if (!line) continue;
      const dataStr = line.slice(5).trim();

      try {
        const parsed = JSON.parse(dataStr);
        if (parsed.type === "meta") onMeta?.(parsed);
        else if (parsed.type === "chunk") onChunk?.(parsed.delta || "");
        else if (parsed.type === "done") onDone?.(parsed);
        else if (parsed.type === "error") onError?.(parsed.message || "Streaming error");
      } catch (parseError) {
        console.error("Failed to parse SSE event", parseError, dataStr);
      }
    }
  }
};

// ----------------- API Keys -----------------
export const createApiKeyApi = async (keyName) => {
  const res = await axiosInstance.post(
    "/auth/api-keys/create",
    { name: keyName },
    //key_name to name
    { headers: { "Content-Type": "application/json" } }
  );
  return res.data;
};

export const listApiKeysApi = async () => {
  const res = await axiosInstance.get("/auth/api-keys/list");
  return res.data;
};

export const revokeApiKeyApi = async (key_id) => {
  const res = await axiosInstance.delete(`/auth/api-keys/${key_id}`);
  return res.data;
};

export const validateApiKeyApi = async (apiKey) => {
  const res = await axiosInstance.post(
    "/auth/api-keys/validate",
    { api_key: apiKey },
    { headers: { "Content-Type": "application/json" } }
  );
  return res.data;
};

// ----------------- Analytics -----------------
export const getHomeDashboardApi = async () => {
  const res = await axiosInstance.get("/analytics/home-dashboard");
  return res.data;
};

export const getAnalyticsDashboardApi = async (days) => {
  const res = await axiosInstance.get(`/analytics/dashboard?days=${days}`);
  return res.data;
};

export const getTopDocumentsApi = async (limit = 5) => {
  const res = await axiosInstance.get(`/analytics/documents/top?limit=${limit}`);
  return res.data;
};

export const getRecentActivityApi = async (limit = 5) => {
  const res = await axiosInstance.get(`/analytics/recent-activity?limit=${limit}`);
  return res.data;
};

export const getAnalyticsSummaryApi = async () => {
  const res = await axiosInstance.get("/analytics/summary");
  return res.data;
};


// Logout API
export const logoutApi = async () => {
  const res = await axiosInstance.post("/auth/logout");

  return res;
};

//Cost Summary
export const getCostSummaryApi = async () => {
  const res = await axiosInstance.get("/cost/summary");
  return res.data;
}

//create payment session
export const createPaymentSessionApi = async (subscription, plantype, coupon) => {
  const params = new URLSearchParams({
    subscription,
    plantype,
    ...(coupon && { coupon }),
  });

  const res = await axiosInstance.post(`/create-rz-session?${params.toString()}`);
  return res;
}

// Verify Razorpay payment
export const verifyPaymentApi = async (razorpayResponse, paymentData) => {
  const params = new URLSearchParams({
    order_id: paymentData.order_id,
    subscription: paymentData.subscription,
    plantype: paymentData.plan_type,
    razorpay_payment_id: razorpayResponse.razorpay_payment_id,
    razorpay_signature: razorpayResponse.razorpay_signature,
  });

  return await axiosInstance.get(`/verify-payment-rz?${params.toString()}`);

};
